import moment from 'moment';
import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { get, isEmpty } from 'lodash';
import { useAtomValue } from 'jotai';
import { useSelector } from 'react-redux';
import { getAlertsSummaryData, getAlertsSummaryDataWithPagination } from '@src/api/aiAlerts';
import { aiAlertsDimensionMappingSelector, intervalSelector } from '@store/common/selectors';
import { dimensionsListSelectorKeyValue } from '@store/common/dimensionList/selectors';
import { aiAlertAtom } from '@src/atoms/toggles';
import { CURRENT_PRODUCT_MODE, isAppModule } from '@src/utils/constants';
import { ALERT_TYPE_CONTEXT_MAP } from '@src/utils/aiAlerts/mapping';
import { trackQueryError } from '@utils/sensor/apptracker';
import { DATA_TAGS, useGetData, useSetData } from '../useData';
import { formatAIAlertSummary } from '@src/components/ai-alerts/formatter';
import { getInvertedPercentageMetricByKey } from '@src/utils/metrics/metricHelpers';
import { globalFeatureNames } from '@src/utils/featureToggle/constants';
import { useGlobalFeature } from '@src/globalStore/globalFeatureFlagsStore';

const defaultValue = {};

const groupByMetric = (alerts) => {
  const metricAlerts = {};
  alerts.forEach((alert) => {
    if (ALERT_TYPE_CONTEXT_MAP[alert.alertType] === CURRENT_PRODUCT_MODE) {
      const invertedPercentMetric = getInvertedPercentageMetricByKey({
        metricKey: alert.metricName,
      });
      const metric = invertedPercentMetric ? invertedPercentMetric : alert.metricName;
      if (metric in metricAlerts) {
        metricAlerts[metric].push(alert);
      } else {
        metricAlerts[metric] = [alert];
      }
    }
  });
  return metricAlerts;
};

function useFetchAIAlerts({ enabled = false, dataTag = DATA_TAGS.aiAlertEvents }) {
  const interval = useSelector(intervalSelector);
  const dimensionListObject = useSelector(dimensionsListSelectorKeyValue);
  const dimensionMapping = useSelector(aiAlertsDimensionMappingSelector);
  const enableDPIAlertPagination = useGlobalFeature(globalFeatureNames.DPI_ALERT_PAGINATION);
  const alertEnabled = useAtomValue(aiAlertAtom);
  const setAiAlertEventData = useSetData({ tag: dataTag, defaultValue });
  const { from, to } = interval;

  // stale time prevents redundant calls from multiple subscribers
  const {
    data: metricAlerts,
    isFetching,
    isError,
  } = useQuery(
    ['ai-alert-events', interval],
    () => {
      const processAlerts = (rawData) => {
        const alerts = formatAIAlertSummary(
          rawData,
          dimensionListObject as any,
          dimensionMapping[CURRENT_PRODUCT_MODE.toLowerCase()],
        );
        return groupByMetric(alerts);
      };

      if (isAppModule && enableDPIAlertPagination) {
        // filter for metricType:[all selected metrics] can be applied,
        // but keeping it same as old logic for now
        const requestBody = {
          from: moment(from).format(),
          to: moment(to).format(),
          getAll: true,
        };
        return getAlertsSummaryDataWithPagination(requestBody)
          .then((response) => get(response, ['data', 'data'], []))
          .then(processAlerts);
      }

      return getAlertsSummaryData({ params: { ...interval } })
        .then((response) => get(response, ['data'], {}))
        .then(processAlerts);
    },
    {
      retry: 3,
      refetchOnWindowFocus: false,
      enabled: enabled && alertEnabled && !!interval,
      staleTime: 5 * 60 * 1000, // 5m
      placeholderData: defaultValue,
      onError: (err) => {
        trackQueryError({ type: 'ai-alert-events', err });
      },
    },
  );

  const data = useMemo(() => {
    // to remove the series markers (toggled)
    if (!alertEnabled || isEmpty(metricAlerts)) {
      return defaultValue;
    }
    return metricAlerts;
  }, [alertEnabled, metricAlerts]);

  useEffect(() => {
    setAiAlertEventData(data);
  }, [data, setAiAlertEventData]);

  return { data, isFetching, isError };
}

export const useAiAlertEventData = ({ metric = null, dataTag = DATA_TAGS.aiAlertEvents }) => {
  const data = useGetData({ tag: dataTag, defaultValue });
  if (metric) {
    return data[metric] || [];
  }
  return data;
};

export default useFetchAIAlerts;
